/**
 * HEAREN — NowPlayingContext
 * 
 * Lightweight global state for persistent audio.
 * soundscapeService is already a singleton — this context just tracks
 * the UI metadata (what's playing, theme, title) so MiniPlayer can render.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import soundscapeService from '@/services/soundscapeService';

const NowPlayingContext = createContext(null);

export const NowPlayingProvider = ({ children }) => {
  const [nowPlaying, setNowPlaying] = useState(null);

  // nowPlaying shape: { title, theme, themeLabel, isPlaying, trackPath, accent,
  //   playerState: { selectedSound, selectedStyle, sounds } }

  const setTrack = useCallback((info) => {
    setNowPlaying((prev) => ({ ...prev, ...info }));
  }, []);

  const clearTrack = useCallback(() => {
    setNowPlaying(null);
  }, []);

  const togglePlayPause = useCallback(() => {
    if (!nowPlaying) return;

    if (nowPlaying.isPlaying) {
      soundscapeService.pause();
      setNowPlaying((prev) => (prev ? { ...prev, isPlaying: false } : null));
    } else {
      soundscapeService.resume();
      setNowPlaying((prev) => (prev ? { ...prev, isPlaying: true } : null));
    }
  }, [nowPlaying]);

  const stopAndClear = useCallback(() => {
    soundscapeService.stop({ reason: 'mini-player-stop' });
    setNowPlaying(null);
  }, []);

  return (
    <NowPlayingContext.Provider
      value={{
        nowPlaying,
        setTrack,
        clearTrack,
        togglePlayPause,
        stopAndClear,
      }}
    >
      {children}
    </NowPlayingContext.Provider>
  );
};

export const useNowPlaying = () => {
  const ctx = useContext(NowPlayingContext);
  if (!ctx) throw new Error('useNowPlaying must be used inside NowPlayingProvider');
  return ctx;
};
